// =============================================================================
// Sites Page — FULL feature set from prototype + RMG requirements:
//   R14: Double-click inline edit
//   R15: Typed cells (string, bool, numeric, date, enum)
//   R17-R19: Bulk update with affected-count + type-matching
//   R20: Dynamic fuzzy search
//   R21: Filter on any column (per-column value filter popovers)
//   R22: Pre-defined risk-based quick filters (slicers)
//   R23: Click-through from dashboard (URL params)
//   R24-R25: Column reorder/hide + Columns panel
//   Persona "user view": the top-bar persona locks the list to that PM's owned sites
//     (server-side owner scope); 'Whole Estate' shows all. Session-scoped store state.
//   R32: Hover a cell → see who last changed it
//   R33: Recent-change highlight (orange wash on recently edited cells)
//   R35: Export filtered table to CSV/Excel
// =============================================================================
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Search, Download, X, ChevronUp, ChevronDown, AlertTriangle,
  Filter, Columns3, ArrowUp, ArrowDown, Check,
} from 'lucide-react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { api } from '../lib/api'
import { useAsync } from '../hooks/useAsync'
import { Button, RagChip, RagDot, Pill, Skeleton, EmptyState, ErrorState } from '../components/ui'
import { Toast } from '../components/Toast'
import { fmtDate, fmtDateTime } from '../lib/format'
import type { Meta, SitesResponse, SiteRow, ColumnDef } from '../lib/types'
import {
  useSitesSlice, initSitesSlice, setOrder as storeSetOrder, setHidden as storeSetHidden,
  setSort as storeSetSort, setDir as storeSetDir, setSearch as storeSetSearch,
  setSlicer as storeSetSlicer, setScroll as storeSetScroll, setColFilters as storeSetColFilters,
  clearFilters as storeClearFilters, stageEdit, stageMany, stageDrawer, clearPending, clearFields,
  pendingCellCount, isSameAsServer,
} from '../store/useSitesSlice'
import { usePersonaSlice } from '../store/usePageSlices'

const ROW_H = 36
const TODAY_MS = Date.now()

// ---- Slicers (R22) ----
const SLICERS = [
  { key: 'jeopardy', label: 'At Risk' },
  { key: 'migrate_no_change', label: 'Migrating · no SNOW CR' },
  { key: 'prereqs_incomplete', label: 'Prereqs incomplete' },
  { key: 'upcoming', label: 'This week' },
  { key: 'no_lld', label: 'No LLD (upcoming)' },
]

// ---- Change highlighting (R33) ----
function changeClass(changedAt: Record<string, string> | undefined, field: string): string {
  const raw = changedAt?.[field]
  if (!raw) return ''
  const iso = raw.split('|')[0]
  const age = TODAY_MS - new Date(iso).getTime()
  if (age <= 24 * 3600000) return 'cell-changed-strong'
  if (age <= 48 * 3600000) return 'cell-changed'
  return ''
}

// ---- Hover tooltip text (R32) ----
function hoverText(changedAt: Record<string, string> | undefined, field: string): string | null {
  const raw = changedAt?.[field]
  if (!raw) return null
  const [iso, who] = raw.split('|')
  return `Last changed ${fmtDateTime(iso)}${who ? ` by ${who}` : ''}`
}

// ---- Cell display value for column filters ----
// Reads the SERVER value only. Filtering is deliberately server-value based (see
// displayRows): a staged edit must not move a row in/out of the view. The cell still
// RENDERS the staged value in blue via effectiveValue elsewhere — this is filter logic.
function filterValue(row: SiteRow, col: ColumnDef): string {
  const v = (row as unknown as Record<string, unknown>)[col.key]
  if (v == null || v === '') return '(empty)'
  if (col.type === 'bool') return v ? 'Yes' : 'No'
  if (col.type === 'date') return fmtDate(v as string)
  return String(v)
}

// Friendly display label for a derived brag value, sourced from the single RAG authority
// (meta.ragBands). null (no migration date) → the 'unscheduled' band's label.
function bragLabel(meta: Meta, brag: string | null): string {
  const token = brag === null ? 'unscheduled' : brag
  return meta.ragBands[token]?.label ?? brag ?? '—'
}

// Column-filter seed for a drill-through from another page. Single source for how each drill param
// (site_id / brag / bb1_circuit_status) maps to a column filter, incl. the unscheduled→'(empty)'
// sentinel — used by both the first-mount slice seed and the re-mount effect so they can't drift.
function seedFromDrill({ siteId, brag, circuitStatus }: { siteId?: string; brag?: string; circuitStatus?: string }): Record<string, string[]> {
  const seed: Record<string, string[]> = {}
  if (siteId) seed.site_id = [siteId]
  if (brag) seed.overall_status_brag = [brag === 'unscheduled' ? '(empty)' : brag]
  if (circuitStatus) seed.bb1_circuit_status = [circuitStatus]
  return seed
}

export function SitesPage({ meta }: { meta: Meta }) {
  const [searchParams, setSearchParams] = useSearchParams()

  // Read URL params synchronously as initial values (R23 click-through)
  const initialSlicer = searchParams.get('slicer') || ''
  const initialBrag = searchParams.get('brag') || ''
  const initialCircuitStatus = searchParams.get('bb1_circuit_status') || ''
  const initialSearch = searchParams.get('search') || ''
  const initialSiteId = searchParams.get('site_id') || '' // exact site ID match

  // One-time seed of the store slice: columns → order/hidden, plus URL-param seeds.
  // Guarded inside initSitesSlice by `initialized`, so this only takes effect on the
  // very first mount — later re-mounts (after navigation) keep the user's state.
  const [initReady] = useState(() => {
    // Drill-through params → column-filter seed (single-sourced in seedFromDrill). A `?site_id=`
    // drill (jeopardy / NBA "open this site") lands on one site via the Site ID column filter,
    // decoupled from the free-text search box, which is now purely fuzzy.
    const seed = seedFromDrill({ siteId: initialSiteId, brag: initialBrag, circuitStatus: initialCircuitStatus })
    initSitesSlice({
      order: meta.columns.map((c) => c.key),
      hidden: meta.columns.filter((c) => c.visible === false).map((c) => c.key),
      colFilters: seed,
      search: initialSearch,
      slicer: initialSlicer,
    })
    return true
  })
  void initReady

  // Active persona "user view" — a locked server-side owner scope (null = Whole Estate,
  // all sites). Global store state, so switching it in the top bar refetches this list.
  const ownerScope = usePersonaSlice((s) => s.persona.ownerScope)

  // ---- View state (persisted in the page store, survives navigation) ----
  const search = useSitesSlice((s) => s.search)
  // Whether a search is *effectively* active — trimmed, matching the server's `searching` predicate
  // (String(q.search).trim() !== ''). Used to gate the relevance-order UI (sort-disable + caption) so
  // a whitespace-only query doesn't claim relevance order while the server actually column-sorts.
  const searchActive = search.trim() !== ''
  const slicer = useSitesSlice((s) => s.slicer)
  const sort = useSitesSlice((s) => s.sort)
  const dir = useSitesSlice((s) => s.dir)
  const order = useSitesSlice((s) => s.order)
  const hiddenArr = useSitesSlice((s) => s.hidden)
  const colFilters = useSitesSlice((s) => s.colFilters)
  const pendingEdits = useSitesSlice((s) => s.pendingEdits)
  const hidden = useMemo(() => new Set(hiddenArr), [hiddenArr])
  const pendingCount = useMemo(() => pendingCellCount(pendingEdits), [pendingEdits])

  // ---- Transient state (local — resets on navigate, which is fine) ----
  const [debounced, setDebounced] = useState(search)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [openSiteId, setOpenSiteId] = useState<number | null>(null)
  // Staged-changes confirmation toast — owned by the page, not the drawer, so it can
  // linger after the drawer closes (the drawer unmounts on close). `stagedToastNonce`
  // bumps on each staging so the toast remounts and re-pulses even if still visible.
  const [stagedToast, setStagedToast] = useState(false)
  const [stagedToastNonce, setStagedToastNonce] = useState(0)
  const stagedToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (stagedToastTimer.current) clearTimeout(stagedToastTimer.current) }, [])
  function showStagedToast() {
    setStagedToast(true)
    setStagedToastNonce((n) => n + 1)
    if (stagedToastTimer.current) clearTimeout(stagedToastTimer.current)
    stagedToastTimer.current = setTimeout(() => setStagedToast(false), 4000)
  }
  const [showCols, setShowCols] = useState(false)
  const [filterPop, setFilterPop] = useState<{ key: string; x: number; y: number } | null>(null)
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(null)
  const [applying, setApplying] = useState(false)
  const [pendingNav, setPendingNav] = useState<string | null>(null) // intercepted destination
  const navigate = useNavigate()
  // Last-clicked position WITHIN displayRows (the filtered+sorted view) — anchors shift-select.
  const anchorIndex = useRef<number | null>(null)

  // Apply incoming URL params to the store on EVERY mount, then clear them. The
  // one-time initSitesSlice seed is guarded by `initialized`, so it only applies
  // params on the very first mount — but a card drill-through (Dashboard → "Open
  // view" → /?slicer=…) lands on a RE-mount, where that guard would drop the seed.
  // A navigation param is an explicit intent and must always win, so apply it here
  // regardless of `initialized`, then strip it so a browser refresh doesn't re-apply.
  useEffect(() => {
    if (initialSlicer || initialBrag || initialCircuitStatus || initialSearch || initialSiteId) {
      // Any drill-through from another page (site / risk cohort / status / RAG) lands on a CLEAN
      // view: stale search, slicer and column filters from a prior visit are cleared first, then
      // ONLY the drill's own selection is applied. Rationale: the user clicking a drill has no idea
      // what filter/search state is sitting in the Sites view (they may not have opened it this
      // session), so any retained state is invisible-to-them narrowing — the table would silently
      // show fewer sites than the figure they clicked. Do NOT re-introduce a "merge with existing
      // filters": there is no workflow that needs it (the source figure is whole-estate, not scoped
      // to the Sites view's filters, so intersecting them makes the count disagree with the card).
      storeSetSearch(initialSearch)
      storeSetSlicer(initialSlicer)
      storeSetColFilters(seedFromDrill({ siteId: initialSiteId, brag: initialBrag, circuitStatus: initialCircuitStatus }))
      setSearchParams({}, { replace: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounce search (R20)
  useEffect(() => { const t = setTimeout(() => setDebounced(search), 250); return () => clearTimeout(t) }, [search])

  // Reset selection AND the shift-select anchor when the on-screen row set or its order
  // changes via search/slicer/filters/sort/dir — a stale id-based selection would count/
  // stamp rows no longer shown, and a stale index anchor would frame the wrong rows.
  // `pendingEdits` is intentionally not a trigger: staging an edit keeps the selection and
  // anchor usable, and under server-value filtering a staged edit never changes the row set.
  useEffect(() => { setSelected(new Set()); anchorIndex.current = null }, [debounced, slicer, colFilters, sort, dir, ownerScope])

  // ---- Data fetch ----
  const { data, loading, error, reload } = useAsync<SitesResponse>(
    () => {
      // pageSize 2000 fetches the whole (owner-scoped) estate in one page — safe at PoC scale
      // (~905 sites). NOTE: the site_id drill-through narrows client-side via the Site ID column
      // filter over THIS fetched set, so if the scoped estate ever exceeds 2000, a drilled site on a
      // later page wouldn't be present and the table would show empty. Revisit the paging model
      // before that scale (TASKS.md). All non-persona filtering is client-side today, so this holds.
      const params: Record<string, string | number | undefined> = { sort, dir, pageSize: 2000 }
      // Free-text search is always the fuzzy `search` param now. A single-site drill-through
      // narrows via the Site ID column filter (client-side over the fetched set), not a server param.
      if (debounced) params.search = debounced
      if (slicer) params.slicer = slicer
      if (ownerScope) params.owner = ownerScope
      return api.sites(params)
    },
    [debounced, slicer, sort, dir, ownerScope],
  )

  const rows = useMemo(() => data?.rows ?? [], [data])
  const total = data?.total ?? 0
  const colByKey = useMemo(() => new Map(meta.columns.map((c) => [c.key, c])), [meta.columns])

  // Drop the shift-select anchor (only) whenever the fetched row set changes — e.g. Apply's
  // reload() returns a re-filtered list where the anchored index now points at a different
  // row. Anchor only, not selection; and NOT keyed on pendingEdits (staging never changes
  // `rows` under server-value filtering), so normal shift-select during editing is unaffected.
  useEffect(() => { anchorIndex.current = null }, [rows])

  // Visible columns (respecting order + hidden)
  const visibleCols = useMemo(
    () => order.map((k) => colByKey.get(k)).filter((c): c is ColumnDef => !!c && !hidden.has(c.key)),
    [order, hidden, colByKey],
  )

  // Client-side column value filters on top of server filters (R21). Filtering reads the
  // SERVER value, never the staged value — a staged edit must NOT move a row in/out of the
  // view (that would eject a row mid-edit and shift shift-select indices). A pending row
  // therefore stays put, showing its staged value in blue, until Apply commits it and the
  // reload re-filters on the new server truth. This matches sort + the server slicers, which
  // are already server-value based. (pendingEdits intentionally absent from the deps.)
  const displayRows = useMemo(() => {
    const active = Object.entries(colFilters).filter(([, v]) => v.length)
    if (!active.length) return rows
    return rows.filter((r) => active.every(([k, allowed]) => {
      const c = colByKey.get(k); if (!c) return true
      return allowed.includes(filterValue(r, c))
    }))
  }, [rows, colFilters, colByKey])

  // Grid sizing
  const gridTemplate = useMemo(() => `36px ${visibleCols.map((c) => `${c.width ?? 130}px`).join(' ')}`, [visibleCols])
  const minWidth = useMemo(() => 36 + visibleCols.reduce((s, c) => s + (c.width ?? 130), 0), [visibleCols])

  // Virtualiser
  const parentRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({ count: displayRows.length, getScrollElement: () => parentRef.current, estimateSize: () => ROW_H, overscan: 20 })

  // Scroll restore: apply the saved scrollTop ONCE, when rows first arrive after mount
  // (restoring the position from before navigating away). Guarded by a ref so later data
  // refetches — e.g. Apply's reload() — don't re-yank the viewport back to the old offset.
  const savedScroll = useSitesSlice((s) => s.scroll)
  const didRestoreScroll = useRef(false)
  useEffect(() => {
    const el = parentRef.current
    if (!el || !data || didRestoreScroll.current) return
    didRestoreScroll.current = true
    if (savedScroll > 0) el.scrollTop = savedScroll
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])
  useEffect(() => {
    const el = parentRef.current
    if (!el) return
    let raf = 0
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => storeSetScroll(el.scrollTop)) }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => { el.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf) }
  }, [])

  // Selection. `anchorIndex` (declared above) is the last-clicked position WITHIN
  // displayRows, so shift-click ranges over what's actually on screen.
  const allSelected = displayRows.length > 0 && displayRows.every((r) => selected.has(r.id))
  function toggleAll() { setSelected(allSelected ? new Set() : new Set(displayRows.map((r) => r.id))); anchorIndex.current = null }
  function toggleRowAt(index: number, shiftKey: boolean) {
    setSelected((prev) => {
      const n = new Set(prev)
      if (shiftKey && anchorIndex.current !== null) {
        // Select the inclusive range between the anchor and this row, by their
        // position in displayRows — "between" = between in the on-screen order.
        const [lo, hi] = anchorIndex.current <= index ? [anchorIndex.current, index] : [index, anchorIndex.current]
        for (let i = lo; i <= hi; i++) { const r = displayRows[i]; if (r) n.add(r.id) }
      } else {
        const id = displayRows[index].id
        n.has(id) ? n.delete(id) : n.add(id)
      }
      return n
    })
    // A shift-click extends from the existing anchor; a plain click sets a new one.
    if (!shiftKey || anchorIndex.current === null) anchorIndex.current = index
  }

  // Sort
  function setSortKey(key: string) { sort === key ? storeSetDir(dir === 'asc' ? 'desc' : 'asc') : (storeSetSort(key), storeSetDir('asc')) }

  // Filters
  const hasFilters = !!(search || slicer || Object.keys(colFilters).length)

  // Export CSV (R35) — server (committed) values. Export is disabled while any edit is
  // pending (see the Export button), so there are never staged values to reconcile here.
  function exportCsv() {
    if (pendingCount > 0) return // guard: button is disabled, but never export a half-edited view
    const headers = visibleCols.map((c) => c.label)
    const lines = [headers.join(',')]
    for (const r of displayRows) { lines.push(visibleCols.map((c) => csvCell((r as Record<string, unknown>)[c.key])).join(',')) }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `sites-${new Date().toISOString().slice(0, 10)}.csv`; a.click()
  }

  // Inline edit → STAGE into the pending buffer (no write, no reload).
  function stageCell(id: number, field: string, value: unknown, serverValue: unknown) {
    stageEdit(id, field, value, serverValue)
  }

  // Apply the whole buffer: one PUT per dirty row (batching that row's fields),
  // last-write-wins (no concurrency token), allSettled so one failure doesn't
  // abort the batch. Succeeded rows are cleared; failed rows stay pending.
  async function applyPending() {
    const entries = Object.entries(pendingEdits)
    if (!entries.length || applying) return
    setApplying(true)
    // Snapshot the exact field→value sent per row NOW. On success we clear only fields whose
    // buffer value is still what we sent — so an edit staged onto a row while its PUT is in
    // flight survives, whether it's a DIFFERENT field or a re-stage of the SAME field to a
    // new value (the applying guard blocks re-Apply, not staging).
    const sentByRow: Record<number, Record<string, unknown>> = {}
    for (const [id, fields] of entries) sentByRow[Number(id)] = { ...(fields as Record<string, unknown>) }
    const results = await Promise.allSettled(
      entries.map(([id, fields]) => api.updateSite(Number(id), fields)),
    )
    const clearedByRow: Record<number, Record<string, unknown>> = {}
    results.forEach((res, i) => { if (res.status === 'fulfilled') { const id = Number(entries[i][0]); clearedByRow[id] = sentByRow[id] } })
    if (Object.keys(clearedByRow).length) clearFields(clearedByRow)
    setApplying(false)
    reload()
  }

  // ---- Leave guards (fire only when the buffer is non-empty) ----
  // In-app navigation: intercept clicks on in-app links (react-router NavLinks
  // render <a href>) at the capture phase, show our own confirm modal, and only
  // navigate on confirm. This works with the component router (BrowserRouter);
  // useBlocker would require migrating to a data router. Confirming discards the
  // staged edits (buffer is in-memory only).
  useEffect(() => {
    if (pendingCount === 0) return
    const handler = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = (e.target as HTMLElement)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return
      // Same-origin, different in-app path only.
      const url = new URL(a.href, window.location.href)
      if (url.origin !== window.location.origin) return
      const dest = url.pathname + url.search + url.hash
      const here = window.location.pathname + window.location.search + window.location.hash
      if (dest === here) return
      e.preventDefault()
      e.stopPropagation()
      setPendingNav(dest)
    }
    document.addEventListener('click', handler, true)
    return () => document.removeEventListener('click', handler, true)
  }, [pendingCount])
  function confirmLeave() { const dest = pendingNav; clearPending(); setPendingNav(null); if (dest) navigate(dest) }
  function cancelLeave() { setPendingNav(null) }

  // Refresh / tab-close: the browser's own generic dialog (text not customisable).
  useEffect(() => {
    if (pendingCount === 0) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [pendingCount])

  return (
    <div className="relative flex h-[calc(100vh-6.5rem)] flex-col gap-2">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div><h1 className="text-base font-semibold text-ink">Site Portal</h1><p className="text-[12px] text-muted">{total.toLocaleString('en-GB')} sites</p></div>
      </div>

      {/* Slicers (R22) */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Quick views</span>
        {SLICERS.map((s) => (
          <button key={s.key} onClick={() => storeSetSlicer(slicer === s.key ? '' : s.key)}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all ${
              slicer === s.key ? 'border-brand-600 bg-brand-50 text-brand-700 shadow-sm' : 'border-line bg-white text-muted hover:border-brand-200 hover:text-ink'}`}>
            {s.key === 'jeopardy' && <AlertTriangle className="h-3 w-3" />}{s.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-line bg-white px-2.5 py-1.5">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
          <input type="text" placeholder="Search site, name, postcode…" value={search} onChange={(e) => storeSetSearch(e.target.value)}
            className={`w-full rounded-md border border-line bg-canvas py-1.5 pl-8 text-[13px] focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-200 ${searchActive ? 'pr-[7.5rem]' : 'pr-3'}`} />
          {/* Fuzzy search orders results by match relevance, so column sort is suspended — say so
              here (paired with the disabled sort headers) rather than leave the change unexplained.
              Gated on searchActive (trimmed) so a whitespace-only query doesn't falsely claim it. */}
          {searchActive && <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-medium uppercase tracking-wide text-muted">Sorted by relevance</span>}
        </div>
        {hasFilters && <Button variant="ghost" size="sm" onClick={storeClearFilters}><X className="h-3.5 w-3.5" /> Clear</Button>}
        <div className="ml-auto flex items-center gap-1.5">
          {/* Column chooser (R24-R25) */}
          <button onClick={() => setShowCols(!showCols)} className="flex h-8 items-center gap-1.5 rounded-md border border-line px-2.5 text-[12px] font-medium text-muted hover:bg-canvas hover:text-ink">
            <Columns3 className="h-3.5 w-3.5" /> Columns
          </button>
          <button onClick={exportCsv} disabled={pendingCount > 0}
            title={pendingCount > 0 ? 'Apply or discard your pending changes before exporting' : undefined}
            className="flex h-8 items-center gap-1.5 rounded-md border border-line px-2.5 text-[12px] font-medium text-muted hover:bg-canvas hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent">
            <Download className="h-3.5 w-3.5" /> Export
          </button>
        </div>
      </div>

      {/* Column chooser panel (R24-R25) */}
      {showCols && (
        <ColumnChooser columns={meta.columns} order={order} setOrder={storeSetOrder} hidden={hidden} setHidden={(s) => storeSetHidden(Array.from(s))} onClose={() => setShowCols(false)} />
      )}

      {/* Grid */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card border border-line bg-white">
        <div ref={parentRef} className="min-h-0 flex-1 overflow-auto">
          <div style={{ minWidth }}>
            {/* Header */}
            <div className="sticky top-0 z-10 grid border-b border-line bg-slate-50/80 backdrop-blur-sm text-[10px] font-semibold uppercase tracking-wider text-muted" style={{ gridTemplateColumns: gridTemplate }}>
              <div className="flex items-center justify-center px-2 py-2">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
              </div>
              {visibleCols.map((c) => (
                <div key={c.key} className="group flex items-center">
                  {/* While a fuzzy search is active the server orders by match relevance, so column
                      sort is suspended: drop the sort chevron + hover affordance and show a
                      not-allowed cursor (the "Sorted by relevance" caption by the search box
                      explains why). The label stays full-strength and the per-column FILTER button
                      below is untouched — only sorting is paused, not the whole column. */}
                  <button onClick={() => setSortKey(c.key)} disabled={searchActive}
                    title={searchActive ? 'Sorted by match relevance while searching' : undefined}
                    className="flex min-w-0 flex-1 items-center gap-0.5 truncate py-2 pl-2 text-left hover:text-ink disabled:cursor-not-allowed disabled:hover:text-muted">
                    <span className="truncate">{c.label}</span>
                    {/* Excel/Sheets convention: ascending (A→Z, 1→9) points DOWN — values grow as you read down the column. */}
                    {!searchActive && sort === c.key && (dir === 'asc' ? <ChevronDown className="h-3 w-3 text-brand-600" /> : <ChevronUp className="h-3 w-3 text-brand-600" />)}
                  </button>
                  {/* Per-column filter button (R21). Hover-reveal: only shown on header-cell hover,
                      unless a filter is active on this column (then always visible + filled). */}
                  <button title="Filter values" onClick={(e) => { e.stopPropagation(); const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); setFilterPop((cur) => cur?.key === c.key ? null : { key: c.key, x: Math.min(r.left, window.innerWidth - 260), y: r.bottom }) }}
                    className={`px-1 py-2 transition-opacity ${colFilters[c.key]?.length ? 'text-brand-600 opacity-100' : 'text-slate-400 opacity-0 hover:text-ink group-hover:opacity-100'}`}>
                    <Filter className="h-3 w-3" fill={colFilters[c.key]?.length ? 'currentColor' : 'none'} />
                  </button>
                </div>
              ))}
            </div>

            {/* Rows */}
            {loading && !data ? (
              <div className="space-y-px p-2">{Array.from({ length: 14 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
            ) : error && !data ? (
              <ErrorState message={error} onRetry={reload} />
            ) : displayRows.length === 0 ? (
              <EmptyState title="No sites match" hint="Try widening filters or clearing the search." />
            ) : (
              // Dim + lock the existing rows during a re-fetch (loading with data already present) so a
              // slicer/filter change reads as "updating" rather than briefly showing the previous result
              // as if it were the new one. Purely presentational — the rows and logic are unchanged.
              <div className={loading ? 'opacity-50 transition-opacity pointer-events-none' : 'transition-opacity'}
                style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
                {virtualizer.getVirtualItems().map((vi) => {
                  const row = displayRows[vi.index]
                  const isSel = selected.has(row.id)
                  return (
                    <div key={row.id} onClick={() => setOpenSiteId(row.id)}
                      className={`absolute left-0 grid w-full cursor-pointer items-center border-b border-line/40 text-[13px] transition-colors ${isSel ? 'bg-brand-50/70' : vi.index % 2 === 0 ? 'bg-white' : 'bg-canvas/30'} hover:bg-brand-50/40`}
                      style={{ gridTemplateColumns: gridTemplate, height: ROW_H, transform: `translateY(${vi.start}px)` }}>
                      <div className="flex items-center justify-center px-2" onClick={(e) => e.stopPropagation()}>
                        {/* onClick (not onChange) so we can read shiftKey for range-select.
                            Shift-select spans the on-screen (filtered+sorted) order. */}
                        <input type="checkbox" checked={isSel} readOnly onClick={(e) => toggleRowAt(vi.index, e.shiftKey)} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600" />
                      </div>
                      {visibleCols.map((c) => (
                        <CellDisplay key={c.key} col={c} row={row} pendingRow={pendingEdits[row.id]}
                          onSave={(v) => stageCell(row.id, c.key, v, (row as Record<string, unknown>)[c.key])}
                          onHover={(e, text) => setTip({ x: e.clientX, y: e.clientY, text })}
                          onLeave={() => setTip(null)} />
                      ))}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
        {data && (
          <div className="flex items-center justify-between border-t border-line px-3 py-1.5 text-[11px] text-muted bg-slate-50/50">
            <span>{displayRows.length.toLocaleString('en-GB')} shown{selected.size > 0 ? ` · ${selected.size} selected` : ''}</span>
            <span>Double-click to edit · Click row for detail · Hover for change history</span>
          </div>
        )}
      </div>

      {/* Bulk edit bar (R17-R19) — STAGES into the pending buffer (no write). Centred at
          the bottom; the pending bar sits bottom-right and is RAISED above this bar's band
          when both show (see PendingBar `raised`) so they never overlap on narrow viewports. */}
      {selected.size > 0 && (
        <BulkEditBar count={selected.size} meta={meta} onClear={() => setSelected(new Set())}
          onStage={(field, value) => {
            const rowById = new Map(rows.map((r) => [r.id, r]))
            stageMany(Array.from(selected), field, value, (id) => (rowById.get(id) as Record<string, unknown> | undefined)?.[field])
            setSelected(new Set())
            // Same confirmation as the drawer — bulk staging is instantaneous (no drawer close
            // to wait on), so fire the toast directly. The nonce re-pulses it on repeat stages.
            showStagedToast()
          }} />
      )}

      {/* Pending changes bar — the sole persistent "you have unapplied edits" status.
          Present iff the buffer is non-empty; disappears on Apply/Discard. When the bulk
          bar is also showing, this is raised onto a higher band to avoid overlapping it. */}
      {pendingCount > 0 && (
        <PendingBar count={pendingCount} applying={applying} raised={selected.size > 0} onApply={applyPending} onDiscard={clearPending} />
      )}

      {/* Tooltip (R32) */}
      {tip && <div className="pointer-events-none fixed z-[60] rounded-md bg-slate-900 px-2.5 py-1.5 text-[11px] text-white shadow-lift" style={{ left: tip.x + 14, top: tip.y + 14 }}>{tip.text}</div>}

      {/* Per-column filter popover (R21) */}
      {filterPop && colByKey.get(filterPop.key) && (
        <ColumnFilterPopover pos={filterPop} col={colByKey.get(filterPop.key)!} rows={rows} meta={meta} selected={colFilters[filterPop.key] ?? []}
          onChange={(vals) => { const n = { ...colFilters }; vals.length ? n[filterPop.key] = vals : delete n[filterPop.key]; storeSetColFilters(n) }}
          onClose={() => setFilterPop(null)} />
      )}

      {/* Site drawer */}
      {openSiteId && <SiteDrawerPlaceholder siteId={openSiteId} meta={meta} pendingRow={pendingEdits[openSiteId]} onClose={() => setOpenSiteId(null)} onStaged={showStagedToast} />}

      {/* Staged-changes confirmation — page-owned so it survives the drawer closing.
          Tells the user the edits landed in the pending bar (press Apply to commit).
          Keyed by nonce so re-staging while it's still up replays the entry animation. */}
      <Toast key={stagedToastNonce} show={stagedToast} message="Changes staged — press Apply to save" position="container-tr" />

      {/* Leave-guard modal (in-app navigation) */}
      {pendingNav && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-[1px] animate-fade-in" onClick={cancelLeave}>
          <div className="w-full max-w-sm rounded-card border border-line bg-white p-5 shadow-lift" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[14px] font-semibold text-ink">Leave without applying?</h3>
            <p className="mt-1.5 text-[12px] text-muted">You have {pendingCount} unapplied change{pendingCount === 1 ? '' : 's'}. Leaving this page will discard {pendingCount === 1 ? 'it' : 'them'}.</p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={confirmLeave}>Discard &amp; leave</Button>
              <Button variant="primary" size="sm" onClick={cancelLeave}>Stay</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Cell with hover tooltip + change highlight + inline edit ----
function CellDisplay({ col, row, pendingRow, onSave, onHover, onLeave }: { col: ColumnDef; row: SiteRow; pendingRow?: Record<string, unknown>; onSave: (v: unknown) => void; onHover: (e: React.MouseEvent, text: string) => void; onLeave: () => void }) {
  const [editing, setEditing] = useState(false)
  const serverValue = (row as Record<string, unknown>)[col.key]
  const isPending = !!pendingRow && col.key in pendingRow
  const value = isPending ? pendingRow![col.key] : serverValue
  // A staged (pending) edit takes visual precedence over the server change-wash.
  const cls = isPending ? 'cell-pending' : changeClass(row.changed_at, col.key)
  const tipText = hoverText(row.changed_at, col.key)

  if (editing && col.editable) {
    return (
      <div className="px-1" onClick={(e) => e.stopPropagation()}>
        <InlineEditor col={col} value={value} onCommit={(v) => { setEditing(false); if (String(v ?? '') !== String(value ?? '')) onSave(v) }} onCancel={() => setEditing(false)} />
      </div>
    )
  }

  return (
    <div className={`flex h-full items-center truncate px-2.5 ${cls} ${col.editable ? 'cursor-text' : ''}`}
      onClick={(e) => { if (col.editable) e.stopPropagation() }}
      onDoubleClick={(e) => { if (col.editable) { e.stopPropagation(); setEditing(true) } }}
      onMouseEnter={tipText ? (e) => onHover(e, tipText) : undefined}
      onMouseLeave={tipText ? onLeave : undefined}>
      <CellContent col={col} value={value} />
    </div>
  )
}

function CellContent({ col, value }: { col: ColumnDef; value: unknown }) {
  if (col.key === 'overall_status_brag') return <RagDot rag={value as string} />
  if (col.key === 'migration_status') return value ? <Pill tone={value === 'Migrated' ? 'success' : value === 'Scheduled' ? 'brand' : 'neutral'}>{String(value)}</Pill> : <span className="text-slate-300">—</span>
  if (col.type === 'date') return <span className="tabular-nums text-ink">{fmtDate(value as string)}</span>
  if (col.type === 'bool') return value == null ? <span className="text-slate-300">—</span> : <span className={value ? 'font-medium text-rag-green' : 'text-slate-400'}>{value ? '✓' : '✗'}</span>
  if (value == null || value === '') return <span className="text-slate-300">—</span>
  if (col.key === 'site_id') return <span className="font-semibold text-brand-700">{String(value)}</span>
  if (col.key === 'site_name') return <span className="truncate font-medium text-ink">{String(value)}</span>
  return <span className="truncate text-ink">{String(value)}</span>
}

function InlineEditor({ col, value, onCommit, onCancel }: { col: ColumnDef; value: unknown; onCommit: (v: unknown) => void; onCancel: () => void }) {
  const ref = useRef<HTMLInputElement | HTMLSelectElement>(null)
  useEffect(() => { ref.current?.focus() }, [])
  const base = 'w-full rounded border-2 border-brand-500 bg-white px-1.5 py-0.5 text-[13px] focus:outline-none shadow-sm'
  if (col.type === 'enum' && col.enum) {
    return <select ref={ref as React.RefObject<HTMLSelectElement>} defaultValue={(value as string) ?? ''} className={base} onBlur={(e) => onCommit(e.target.value || null)} onChange={(e) => onCommit(e.target.value || null)} onKeyDown={(e) => { if (e.key === 'Escape') onCancel() }}><option value="">—</option>{col.enum.map((o) => <option key={o} value={o}>{o}</option>)}</select>
  }
  if (col.type === 'bool') {
    return <select ref={ref as React.RefObject<HTMLSelectElement>} defaultValue={value == null ? '' : value ? 'true' : 'false'} className={base} onBlur={(e) => onCommit(e.target.value === '' ? null : e.target.value === 'true')} onChange={(e) => onCommit(e.target.value === '' ? null : e.target.value === 'true')} onKeyDown={(e) => { if (e.key === 'Escape') onCancel() }}><option value="">—</option><option value="true">Yes</option><option value="false">No</option></select>
  }
  const t = col.type === 'date' ? 'date' : col.type === 'int' ? 'number' : 'text'
  return <input ref={ref as React.RefObject<HTMLInputElement>} type={t} defaultValue={(value as string) ?? ''} className={base} onBlur={(e) => onCommit(e.target.value || null)} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') onCancel() }} />
}

// ---- Column filter popover (Excel-style per-column value filter) (R21) ----
function ColumnFilterPopover({ pos, col, rows, meta, selected, onChange, onClose }: { pos: { x: number; y: number }; col: ColumnDef; rows: SiteRow[]; meta: Meta; selected: string[]; onChange: (v: string[]) => void; onClose: () => void }) {
  const [q, setQ] = useState('')
  // Distinct SERVER values (filtering is server-value based — see displayRows). The
  // "Current Stage" column sorts chronologically by the stage plan order; every other
  // column sorts alpha/numerically.
  const distinct = useMemo(() => {
    const vals = Array.from(new Set(rows.map((r) => filterValue(r, col))))
    if (col.key === 'stage_label') {
      const rank = new Map<string, number>()
      meta.stages.forEach((s, i) => rank.set(s.label, i))
      Object.values(meta.boundaryStates).forEach((b) => {
        // Pending Scheduling before the spine; Migration Activities Completed after it.
        rank.set(b.label, b.key === 'pending_scheduling' ? -1 : meta.stages.length)
      })
      return vals.sort((a, b) => (rank.get(a) ?? 999) - (rank.get(b) ?? 999) || a.localeCompare(b))
    }
    return vals.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  }, [rows, col, meta])
  const shown = distinct.filter((d) => d.toLowerCase().includes(q.toLowerCase()))
  const working = selected.length ? new Set(selected) : new Set(distinct)
  function commit(next: Set<string>) { onChange(next.size === distinct.length ? [] : Array.from(next)) }
  function toggle(v: string) { const n = new Set(working); n.has(v) ? n.delete(v) : n.add(v); commit(n) }
  function selectOnly(v: string) { commit(new Set([v])) }
  function selectAll() { onChange([]) }          // [] == all selected (no filter)
  function unselectAll() { onChange(['__rmg_none__']) } // an unmatchable token → shows nothing
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="fixed z-50 w-60 rounded-md border border-line bg-white p-2 shadow-lift animate-slide-down" style={{ left: pos.x, top: pos.y + 4 }}>
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-[11px] font-semibold text-ink">Filter · {col.label}</span>
          <div className="flex gap-2">
            <button onClick={selectAll} className="text-[10px] text-brand-600 hover:underline">All</button>
            <button onClick={unselectAll} className="text-[10px] text-brand-600 hover:underline">None</button>
          </div>
        </div>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="mb-1.5 w-full rounded border border-line px-2 py-1 text-[12px] focus:border-brand-500 focus:outline-none" />
        <div className="max-h-52 overflow-auto">
          {shown.length === 0 && <p className="px-1 py-2 text-[11px] text-muted">No values</p>}
          {shown.map((v) => (
            <label key={v} className="group flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[12px] hover:bg-canvas">
              <input type="checkbox" checked={working.has(v)} onChange={() => toggle(v)} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600" />
              <span className="truncate text-ink">{v}</span>
              <button onClick={(e) => { e.preventDefault(); selectOnly(v) }} className="ml-auto shrink-0 text-[10px] text-brand-600 opacity-0 hover:underline group-hover:opacity-100">only</button>
            </label>
          ))}
        </div>
      </div>
    </>
  )
}

// ---- Column chooser panel (R24-R25) ----
function ColumnChooser({ columns, order, setOrder, hidden, setHidden, onClose }: { columns: ColumnDef[]; order: string[]; setOrder: (o: string[]) => void; hidden: Set<string>; setHidden: (s: Set<string>) => void; onClose: () => void }) {
  const [q, setQ] = useState('')
  const byKey = new Map(columns.map((c) => [c.key, c]))
  function move(key: string, delta: number) { const arr = [...order]; const idx = arr.indexOf(key); const to = idx + delta; if (to < 0 || to >= arr.length) return; arr.splice(idx, 1); arr.splice(to, 0, key); setOrder(arr) }
  function toggle(key: string) { const n = new Set(hidden); n.has(key) ? n.delete(key) : n.add(key); setHidden(n) }
  const ql = q.trim().toLowerCase()
  // Filter only the rendered list; move/hide still operate on the full order.
  const shownKeys = ql ? order.filter((k) => (byKey.get(k)?.label ?? '').toLowerCase().includes(ql)) : order
  return (
    <div className="animate-slide-down rounded-card border border-line bg-white p-3 shadow-card">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[12px] font-semibold text-ink">Columns</span>
        <button onClick={onClose} className="text-[11px] text-brand-600 hover:underline">Done</button>
      </div>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search columns…" className="mb-2 w-full rounded border border-line px-2 py-1 text-[12px] focus:border-brand-500 focus:outline-none" />
      <div className="grid grid-cols-2 gap-1 max-h-60 overflow-auto lg:grid-cols-3">
        {shownKeys.length === 0 && <p className="col-span-full px-1 py-2 text-[11px] text-muted">No columns match</p>}
        {shownKeys.map((key) => {
          const c = byKey.get(key); if (!c) return null
          const visible = !hidden.has(key)
          return (
            <div key={key} className="flex items-center gap-1.5 rounded px-1.5 py-1 hover:bg-canvas text-[12px]">
              <button onClick={() => toggle(key)} className={`flex h-4 w-4 items-center justify-center rounded border ${visible ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300'}`}>
                {visible && <Check className="h-2.5 w-2.5" />}
              </button>
              <span className={visible ? 'text-ink' : 'text-muted'}>{c.label}</span>
              <div className="ml-auto flex gap-0.5">
                <button onClick={() => move(key, -1)} className="text-slate-300 hover:text-ink"><ArrowUp className="h-3 w-3" /></button>
                <button onClick={() => move(key, 1)} className="text-slate-300 hover:text-ink"><ArrowDown className="h-3 w-3" /></button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---- Bulk edit bar (R17-R19) — stamps a field/value across the selection into
// the pending buffer (does not write). Sits centred at the bottom; the pending bar is
// raised above this bar's band when both show, so they don't overlap.
function BulkEditBar({ count, meta, onStage, onClear }: { count: number; meta: Meta; onStage: (field: string, value: unknown) => void; onClear: () => void }) {
  const [fieldKey, setFieldKey] = useState('')
  const [value, setValue] = useState<unknown>(null)
  const def = meta.bulkEditableFields.find((f) => f.field === fieldKey)
  useEffect(() => { setValue(def?.type === 'bool' ? false : null) }, [def])
  return (
    <div className="no-print fixed inset-x-0 bottom-4 z-30 flex justify-center px-4">
      <div className="flex w-full max-w-3xl items-center gap-2.5 rounded-lg border border-brand-200 bg-white px-4 py-2.5 shadow-lift">
        <span className="flex items-center gap-1.5 text-[13px] font-semibold text-ink">
          <span className="flex h-5 min-w-5 items-center justify-center rounded bg-brand-600 px-1.5 text-[10px] font-bold text-white">{count}</span> selected
        </span>
        <select value={fieldKey} onChange={(e) => setFieldKey(e.target.value)} className="rounded-md border border-line px-2.5 py-1.5 text-[12px]"><option value="">Choose field…</option>{meta.bulkEditableFields.map((f) => <option key={f.field} value={f.field}>{f.label}</option>)}</select>
        {def && <BulkValue def={def} value={value} onChange={setValue} />}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onClear}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={!def} onClick={() => { if (!def) return; onStage(def.field, value); setFieldKey('') }}>
            Stage to {count}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ---- Pending changes bar — the sole persistent "unapplied edits" status ----
// Compact, bottom-right; its mere presence signals unapplied edits. When the bulk bar is
// also showing (`raised`), lift onto a higher band so the two never overlap on narrow widths.
function PendingBar({ count, applying, raised, onApply, onDiscard }: { count: number; applying: boolean; raised: boolean; onApply: () => void; onDiscard: () => void }) {
  return (
    <div className={`no-print fixed right-3 z-30 ${raised ? 'bottom-[4.75rem]' : 'bottom-3'}`}>
      <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 shadow-lift">
        <span className="flex items-center gap-1 text-[11px] font-medium text-ink">
          <span className="flex h-4 min-w-4 items-center justify-center rounded bg-amber-500 px-1 text-[9px] font-bold text-white">{count}</span>
          pending change{count === 1 ? '' : 's'}
        </span>
        <button disabled={applying} onClick={onDiscard} className="rounded px-1.5 py-0.5 text-[10px] font-medium text-muted hover:text-ink hover:bg-white/60 disabled:opacity-50">Discard</button>
        <button disabled={applying} onClick={onApply} className="rounded bg-brand-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
          {applying ? 'Applying…' : `Apply ${count}`}
        </button>
      </div>
    </div>
  )
}

function BulkValue({ def, value, onChange }: { def: Meta['bulkEditableFields'][number]; value: unknown; onChange: (v: unknown) => void }) {
  const base = 'rounded-md border border-line px-2.5 py-1.5 text-[12px]'
  if (def.type === 'bool') return <select value={value ? 'true' : 'false'} onChange={(e) => onChange(e.target.value === 'true')} className={base}><option value="true">Yes</option><option value="false">No</option></select>
  if (def.type === 'enum' && def.enum) return <select value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value || null)} className={base}><option value="">—</option>{def.enum.map((o) => <option key={o} value={o}>{o}</option>)}</select>
  return <input type={def.type === 'date' ? 'date' : def.type === 'int' ? 'number' : 'text'} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value || null)} className={base} />
}

// ---- Site detail drawer (proper section-based form, not JSON dump) ----
// "Stage changes" writes the drawer's diffs into the SHARED pending buffer (does
// not commit); Apply on the table's pending bar commits everything. Opening the
// drawer on a row that already has pending edits carries them in.
function SiteDrawerPlaceholder({ siteId, meta, pendingRow, onClose, onStaged }: { siteId: number; meta: Meta; pendingRow?: Record<string, unknown>; onClose: () => void; onStaged: () => void }) {
  const { data, loading } = useAsync(() => api.site(siteId), [siteId])
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const [draft, setDraft] = useState<Record<string, unknown>>({})
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => { setDraft({}); setActiveSection(null) }, [siteId])
  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }; document.addEventListener('keydown', h); return () => document.removeEventListener('keydown', h) }, [onClose])
  // Clear the pending stage→close timer if the drawer unmounts or switches site,
  // so a timer armed for one drawer can't fire onClose() against the next.
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current) }, [])

  const isDirty = Object.keys(draft).length > 0
  // The value already staged in the shared buffer (if any), else the server value.
  const baseVal = (field: string) => (pendingRow && field in pendingRow ? pendingRow[field] : data?.[field])

  function stage() {
    if (!isDirty) return
    // Stage each drawer diff into the shared buffer, diffed against the SERVER value
    // (so returning a field to its server value clears it from the buffer).
    // NOTE: the drawer diffs against its own full-site fetch (api.site → `data`), while
    // inline/bulk edits diff against the list-row value. This relies on the two endpoints
    // (/api/sites and /api/sites/:id) returning the SAME representation for every editable
    // field. They do today (verified: dates YYYY-MM-DD, ints as numbers, null as null). If
    // that ever diverges, the two paths could classify the same input differently — keep the
    // editable-field serialisation identical across both endpoints.
    stageDrawer(siteId, draft, (field) => data?.[field])
    setDraft({})
    // Close the drawer promptly, then fire the page-owned confirmation toast only once
    // the close has completed — so the toast never overlaps the closing drawer. The toast
    // is owned by the page, so it appears after this drawer unmounts and lingers on its own.
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => { onClose(); onStaged() }, 400)
  }

  // getVal overlays: local draft first, then the shared buffer, then server data.
  function getVal(field: string) { return field in draft ? draft[field] : baseVal(field) }
  function setVal(field: string, value: unknown) {
    setDraft((prev) => {
      const n = { ...prev }
      // Diff against the effective base (buffer-or-server) using the SAME equality the
      // buffer uses (int/numeric-string aware), so re-entering a value doesn't leave a
      // phantom dirty field.
      if (isSameAsServer(value, baseVal(field))) delete n[field]
      else n[field] = value
      return n
    })
  }

  // The drawer renders directly from meta.sections (served by /api/meta) — the single source
  // for the site-detail layout, shared with nothing else (grep-confirmed sole consumer). Each
  // FieldDef carries its own { field, label, type, enum }, so there is no drawer-local section
  // list, label map, or type classifier to drift from the server.

  return (
    <div className="fixed inset-0 z-40 flex">
      <button className="flex-1 animate-fade-in bg-slate-900/30 backdrop-blur-[1px]" onClick={onClose} />
      <div className="flex w-full max-w-2xl animate-slide-in flex-col bg-white border-l border-line shadow-lift">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <div>
            <h2 className="text-[15px] font-semibold text-ink">{loading ? 'Loading…' : `${data?.site_id} · ${data?.site_name || ''}`}</h2>
            {data && (
              <div className="mt-1 flex items-center gap-2">
                <RagChip rag={data.overall_status_brag} size="sm" label={bragLabel(meta, data.overall_status_brag)} />
                <span className="text-[11px] text-muted">{data.stage_label}</span>
                {data.jeopardy && <Pill tone="warn">At risk</Pill>}
                {data.next_owner && <span className="text-[10px] text-slate-400">Next: {data.next_owner}</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-muted hover:bg-canvas hover:text-ink text-xl">✕</button>
        </div>

        {/* Section nav buttons (R28 — jump to section) */}
        {data && (
          <div className="flex flex-wrap gap-1 border-b border-line px-5 py-2 bg-canvas/50">
            {meta.sections.map((sec) => (
              <button key={sec.key} onClick={() => { setActiveSection(sec.key); document.getElementById(`sec-${sec.key}`)?.scrollIntoView({ behavior: 'smooth' }) }}
                className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors ${activeSection === sec.key ? 'bg-brand-600 text-white' : 'bg-white border border-line text-muted hover:text-ink'}`}>
                {sec.label}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-auto px-5 py-4">
          {loading ? <Skeleton className="h-40" /> : data ? (
            <div className="space-y-4">
              {meta.sections.map((sec) => (
                <div key={sec.key} id={`sec-${sec.key}`} className="rounded-card border border-line overflow-hidden">
                  <div className="bg-canvas/70 px-3 py-2 border-b border-line">
                    <h3 className="text-[12px] font-semibold text-ink">{sec.label}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 px-3 py-3">
                    {sec.fields.map((f) => {
                      const field = f.field
                      // Honour 'enum' only when values exist, else fall back to a text input —
                      // an enum with no value list would otherwise render as a broken empty
                      // dropdown (a silent R29 regression).
                      const isEnum = f.type === 'enum' && !!f.enum?.length
                      const type = isEnum ? 'enum' : f.type
                      const enumVals = isEnum ? f.enum : undefined
                      const val = getVal(field)
                      // Dirty if edited in the drawer draft OR already staged in the shared buffer.
                      const isDirtyField = field in draft || (!!pendingRow && field in pendingRow)
                      const isReadonly = field === 'site_id'
                      const selCls = 'w-full rounded border border-line px-2 py-1.5 text-[12px] bg-white focus:border-brand-500 focus:outline-none'
                      return (
                        <div key={field} className={type === 'textarea' ? 'col-span-2' : ''}>
                          <label className="mb-1 flex items-center gap-1 text-[11px] font-medium text-muted">
                            {f.label}
                            {isDirtyField && <span className="h-1.5 w-1.5 rounded-full bg-brand-600" />}
                          </label>
                          {isReadonly ? (
                            <span className="text-[13px] font-medium text-ink">{String(val || '—')}</span>
                          ) : type === 'bool' ? (
                            <select value={val == null ? '' : val ? 'true' : 'false'} onChange={(e) => setVal(field, e.target.value === '' ? null : e.target.value === 'true')} className={selCls}>
                              <option value="">—</option><option value="true">Yes</option><option value="false">No</option>
                            </select>
                          ) : type === 'enum' && enumVals ? (
                            <select value={(val as string) ?? ''} onChange={(e) => setVal(field, e.target.value || null)} className={selCls}>
                              <option value="">—</option>{enumVals.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : type === 'textarea' ? (
                            <textarea value={(val as string) || ''} onChange={(e) => setVal(field, e.target.value || null)} rows={2}
                              className="w-full rounded border border-line px-2 py-1.5 text-[12px] bg-white focus:border-brand-500 focus:outline-none resize-y" />
                          ) : (
                            <input type={type === 'date' ? 'date' : type === 'int' ? 'number' : 'text'} value={(val as string) ?? ''} onChange={(e) => setVal(field, e.target.value || null)}
                              className="w-full rounded border border-line px-2 py-1.5 text-[12px] bg-white focus:border-brand-500 focus:outline-none" />
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Stage bar — edits are staged into the shared buffer, applied from the table. */}
        <div className="flex items-center justify-between border-t border-line bg-white px-5 py-2.5">
          <span className="text-[12px] text-muted">{isDirty ? `${Object.keys(draft).length} change${Object.keys(draft).length > 1 ? 's' : ''} to stage` : 'No changes'}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button variant="primary" disabled={!isDirty} onClick={stage}>Stage changes</Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function csvCell(v: unknown): string { if (v == null) return ''; const s = String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
